import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin } from "lucide-react";
import { Reveal, SectionHead } from "./shared";

const FILTERS = ["All", "Remodels", "Decks", "Additions", "Fencing", "Roofing", "Tree Services", "Handyman"];

const PROJECTS = [
  {
    title: "Full Kitchen Rebuild",
    cat: "Remodels",
    img: "https://images.unsplash.com/photo-1628745277862-bc0b2d68c50c?crop=entropy&cs=srgb&fm=jpg&q=85",
    tag: "Kitchen · Drywall · Flooring",
  },
  {
    title: "Two-Story Deck & Pergola",
    cat: "Decks",
    img: "https://images.unsplash.com/photo-1781087837466-95ee7fa5e0c8?crop=entropy&cs=srgb&fm=jpg&q=85",
    tag: "Composite · Railings",
  },
  {
    title: "Backyard Addition Framing",
    cat: "Additions",
    img: "https://images.pexels.com/photos/39151698/pexels-photo-39151698.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    tag: "Foundation · Framing",
  },
  {
    title: "Privacy Fence & Gate",
    cat: "Fencing",
    img: "https://images.pexels.com/photos/11903184/pexels-photo-11903184.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    tag: "Privacy · Gate",
  },
  {
    title: "Full Roof Replacement",
    cat: "Roofing",
    img: "https://images.pexels.com/photos/37677394/pexels-photo-37677394.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    tag: "Tear-off · Shingle",
  },
  {
    title: "Hazard Tree Removal",
    cat: "Tree Services",
    img: "https://images.unsplash.com/photo-1684332666088-f35b252b5aca?crop=entropy&cs=srgb&fm=jpg&q=85",
    tag: "Removal · Haul-off",
  },
  {
    title: "Handyman Repairs Roundup",
    cat: "Handyman",
    img: "https://images.pexels.com/photos/34938432/pexels-photo-34938432.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    tag: "Repairs · Fixtures · Punch List",
  },
];

export default function Work() {
  const [filter, setFilter] = useState("All");
  const shown = PROJECTS.filter((p) => filter === "All" || p.cat === filter);

  return (
    <section id="work" data-testid="work-section" className="py-20 md:py-32 bg-ink-card/40">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-16">
        <SectionHead
          eyebrow="Recent work"
          title="Built, not just bid."
          sub="A sample of what we've finished for homeowners — kitchens to rooftops, all photographed on the job."
        />

        <Reveal delay={0.15}>
          <div className="flex flex-wrap gap-2 mt-10" data-testid="work-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`work-filter-${f.toLowerCase().replace(/ /g, "-")}`}
                className={`font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border transition-all duration-200 ${
                  filter === f ? "border-safety bg-safety text-ink font-bold" : "border-line text-steel hover:text-bone hover:border-steel"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-12">
          <AnimatePresence mode="popLayout">
            {shown.map((p) => (
              <motion.article
                layout
                key={p.title}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`work-card-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group relative clip-notch overflow-hidden bg-ink-card border border-line"
              >
                <div className="overflow-hidden aspect-[4/3]">
                  <img
                    src={p.img}
                    alt={p.title}
                    data-testid={`work-image-${p.cat.toLowerCase().replace(/ /g, "-")}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent opacity-80" aria-hidden="true" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-safety font-bold flex items-center gap-2">
                    <MapPin className="w-3 h-3" /> {p.cat}
                  </p>
                  <h3 className="font-cond font-bold uppercase text-2xl text-bone mt-2 tracking-wide">{p.title}</h3>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-steel mt-1">{p.tag}</p>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
